import { addDays, daysBetween, previousRange, type Category, type Report } from '@fin/shared';

export type ReportRow = Report['byCategory'][number];
export interface CategoryGroup { row: ReportRow; total: number; count: number; children: ReportRow[] }

/** Keep a parent visible even when all its operations live in its children. */
export function groupCategories(list: ReportRow[], categories: Pick<Category, 'id' | 'name' | 'kind' | 'color' | 'parentId'>[] = []): CategoryGroup[] {
  const rows = new Map(list.map(c => [c.categoryId, c]));
  const known = new Map(categories.map(c => [c.id, c]));
  const groups = new Map<number, CategoryGroup>();
  for (const c of list) {
    const parent = c.parentId ? rows.get(c.parentId) : undefined;
    const ref = c.parentId ? known.get(c.parentId) : undefined;
    const row: ReportRow = parent ?? (ref ? { categoryId: ref.id, name: ref.name, kind: ref.kind, color: ref.color, parentId: ref.parentId, total: 0, count: 0 } : c);
    const group = groups.get(row.categoryId) ?? { row, total: 0, count: 0, children: [] };
    group.total += c.total;
    group.count += c.count;
    if (row.categoryId !== c.categoryId) group.children.push(c);
    groups.set(row.categoryId, group);
  }
  return [...groups.values()].sort((a, b) => Math.abs(b.total) - Math.abs(a.total));
}

export function amountChange(current: number, previous: number | null | undefined) {
  if (previous == null) return null;
  const delta = current - previous;
  return { delta, ratio: previous > 0 ? delta / previous : null };
}

export function categoryComparisonRange(today: string) {
  const from = today.slice(0, 7) + '-01';
  return { from, to: today, previous: previousRange(from, today), elapsed: daysBetween(from, today) };
}

export function categoryTotal(rows: ReportRow[], categoryId: number) {
  return rows.filter(c => c.categoryId === categoryId || c.parentId === categoryId).reduce((sum, c) => sum + Math.abs(c.total), 0);
}

export function monthRange(month: string, today: string) {
  const from = month + '-01';
  const end = new Date(Date.UTC(Number(month.slice(0, 4)), Number(month.slice(5, 7)), 1)).toISOString().slice(0, 10);
  return { from, to: end > today ? today : addDays(end, -1) };
}
